import theme from '@hds/auto-auction-com-themes'
import { useLanguageStore, useThemeStore } from '@hds/common-ui-lib/desktop'
import { completeBootAuth, sessionStore } from '@hds/identity-client-sdk'
import { identityMessages } from '@hds/identity-client-sdk/messages'
import { createPinia } from 'pinia'
import PrimeVue from 'primevue/config'
import { createApp } from 'vue'
import { createI18n } from 'vue-i18n'
import { NavigationFailureType, isNavigationFailure } from 'vue-router'

import App from './App.vue'
import { configureBackend } from './backend.js'
import { createAuthGuard } from './identity/authGuard.js'
import { placeBootOutcome } from './identity/bootOutcome.js'
import router from './router/index.js'

import 'primeicons/primeicons.css'
import './assets/collection-layout.css'

async function initializeApp() {
    // Before the app is created, so nothing can render against an unconfigured
    // SDK. Awaited: where the services are is discovered, not built in.
    await configureBackend()

    const app = createApp(App)

    // Pinia must be installed before any store() call resolves, and the
    // session store needs to hydrate before the auth guard is registered —
    // otherwise the first navigation classifies the person as signed out and
    // bounces a real session to the hosted login.
    app.use(createPinia())

    // The identity screens and the shared shell read their copy through
    // vue-i18n. Only the identity namespace is loaded — the shell brings its own
    // words — in both languages, which is what puts both on the Settings page.
    // This app's own pages carry their English in their templates until they
    // are translated; a second catalog would be a second place for it to drift.
    app.use(createI18n({ legacy: false, locale: 'en-CA', fallbackLocale: 'en-CA', messages: identityMessages }))

    // The person's saved theme and language, applied before the first screen.
    // Each store applies what was saved when it is first used; the language
    // store reads the vue-i18n installed above.
    useThemeStore()
    useLanguageStore()

    // The SDK's boot-auth sequence: paint from the session cache, complete the
    // hosted-login return trip (if this load is one), resolve the login once at
    // the callback, and classify the outcome. placeBootOutcome turns that into
    // where the app goes; the guard reads it at navigation time.
    const session = sessionStore()
    const outcome = placeBootOutcome(await completeBootAuth({ session }))

    // Register the guard BEFORE `app.use(router)`. `use(router)` triggers
    // vue-router's initial navigation immediately; a guard added after that
    // would not run for it, and a signed-out person would reach a page that
    // fires administration calls with no Authorization header.
    router.beforeEach(createAuthGuard({ session, outcome }))
    app.use(router)

    // The brand lives in the preset, not in this file and not in raw `--p-*`
    // overrides — see @hds/auto-auction-com-themes. Both schemes are first-class;
    // `.p-dark` on the root element is what switches them.
    app.use(PrimeVue, {
        theme: {
            preset: theme,
            options: {
                darkModeSelector: '.p-dark'
            }
        }
    })

    try {
        await router.isReady()
    } catch (error) {
        // The guard aborts the initial navigation when it sends a signed-out
        // person to the hosted login: the page is already leaving, and there is
        // nothing to mount. Anything else is a real boot failure.
        if (isNavigationFailure(error, NavigationFailureType.aborted)) {
            return
        }
        throw error
    }
    app.mount('#app')

    // A login that resolved to a Human goes where the person was headed — or
    // to the link-outcome verdict a parked connect ended on.
    if (outcome.loginTargetPath && session.isAuthenticated) {
        router.replace(outcome.loginTargetPath).catch(() => {})
    }
}

initializeApp()
